import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SidebarProvider, useSidebar } from "../../components/ui/Sidebar";
import Sidebar from "../../components/ui/Sidebar";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import toast from "react-hot-toast";
import { useGetVendors } from "pages/vendor-management/useGetVendors";
import {
  useCreatePurchaseOrder,
  useUpdatePurchaseOrder,
} from "./usePurchaseOrderMutations";

// ── Constants ─────────────────────────────────────────────────────────────────
const VAT_RATES = [
  { value: 0, label: "0% (Exempt)" },
  { value: 15, label: "15% (Standard)" },
  { value: "custom", label: "Custom" },
];

const ADDITIONAL_CHARGES = [
  {
    key: "shippingCharges",
    label: "Shipping Charges",
    icon: "Truck",
    type: "add",
  },
  { key: "convenienceFee", label: "Convenience Fee", icon: "Tag", type: "add" },
  {
    key: "courierCharges",
    label: "Courier Charges",
    icon: "Package",
    type: "add",
  },
  {
    key: "deliveryCharges",
    label: "Delivery Charges",
    icon: "MapPin",
    type: "add",
  },
  { key: "gratuity", label: "Gratuity", icon: "Heart", type: "add" },
  { key: "retention", label: "Retention", icon: "Minus", type: "deduct" },
  { key: "roundOff", label: "Round Off", icon: "RefreshCw", type: "deduct" },
];

const EMPTY_LINE = () => ({
  id: Date.now() + Math.random(),
  description: "",
  qty: 1,
  rate: "",
  discountType: "percent",
  discount: "",
  vatRate: 15,
  customVat: "",
  showCustomVat: false,
});

const EMPTY_FORM = {
  to: "",
  vendorId: "",
  vendorName: "",
  poPrefix: "PO",
  poNumber: "",
  date: new Date().toISOString().split("T")[0],
  reference: "",
  lines: [],
  enabledCharges: {},
  charges: {},
};

// ── Calculation ───────────────────────────────────────────────────────────────
const calcLine = (line) => {
  const qty = parseFloat(line.qty) || 0;
  const rate = parseFloat(line.rate) || 0;
  const discount = parseFloat(line.discount) || 0;
  const vatRate = line.showCustomVat
    ? parseFloat(line.customVat) || 0
    : parseFloat(line.vatRate) || 0;

  const subtotal = qty * rate;
  const discountAmount =
    line.discountType === "percent"
      ? subtotal * (discount / 100)
      : Math.min(discount, subtotal);
  const afterDiscount = Math.max(0, subtotal - discountAmount);
  const vatAmount = afterDiscount * (vatRate / 100);
  const total = afterDiscount + vatAmount;

  return { subtotal, discountAmount, afterDiscount, vatAmount, total, vatRate };
};

// ── Section Card wrapper (matches invoice wizard style) ───────────────────────
const SectionCard = ({
  icon,
  iconBg,
  iconColor,
  title,
  subtitle,
  defaultExpanded = true,
  children,
  action,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 ${iconBg} rounded-lg flex items-center justify-center`}
            >
              <Icon name={icon} size={20} className={iconColor} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground dark:text-white">
                {title}
              </h3>
              <p className="text-sm text-muted-foreground dark:text-gray-300">
                {subtitle}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {action}
            <Button
              variant="ghost"
              size="sm"
              iconName={expanded ? "ChevronUp" : "ChevronDown"}
              onClick={() => setExpanded(!expanded)}
            />
          </div>
        </div>
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// ── Field helpers ─────────────────────────────────────────────────────────────
const Field = ({ label, required, hint, error, children }) => (
  <div>
    {label && (
      <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
    )}
    {children}
    {hint && !error && (
      <p className="text-xs text-muted-foreground mt-1">{hint}</p>
    )}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const TextInput = ({ error, className = "", ...props }) => (
  <input
    {...props}
    className={`w-full px-3 py-2.5 border rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent text-sm transition-colors ${
      error ? "border-red-400" : "border-gray-200 dark:border-gray-600"
    } ${className}`}
  />
);

const SelectInput = ({ children, className = "", ...props }) => (
  <select
    {...props}
    className={`w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent text-sm ${className}`}
  >
    {children}
  </select>
);

// ── Wizard Content ────────────────────────────────────────────────────────────
const POWizardContent = () => {
  const { isCollapsed } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("pages");
  const editingPO = location.state?.editingPO || null;

  const [form, setForm] = useState({ ...EMPTY_FORM, lines: [EMPTY_LINE()] });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const { data: vendors = [] } = useGetVendors();
  const createPO = useCreatePurchaseOrder();
  const updatePO = useUpdatePurchaseOrder();

  useEffect(() => {
    if (editingPO) {
      const raw = editingPO.raw || editingPO;
      const mappedLines =
        Array.isArray(raw.lineItems) && raw.lineItems.length
          ? raw.lineItems.map((l) => ({
              id: Date.now() + Math.random(),
              description: l.description || "",
              qty: l.quantity ?? 1,
              rate: l.unitPrice ?? "",
              discountType: "fixed",
              discount: l.discountAmount || "",
              vatRate: l.taxRate ?? 15,
              customVat: "",
              showCustomVat: false,
            }))
          : editingPO.lines?.length
            ? editingPO.lines
            : [EMPTY_LINE()];

      const issue = (editingPO.date || raw.issueDate || "")
        .toString()
        .split("T")[0];

      setForm({
        ...EMPTY_FORM,
        vendorId: editingPO.vendorId || raw.vendorId || "",
        vendorName: editingPO.vendorName || raw.vendorName || "",
        poNumber: editingPO.poNumber || raw.purchaseOrderNumber || "",
        date: issue || new Date().toISOString().split("T")[0],
        lines: mappedLines,
        enabledCharges: editingPO.enabledCharges || {},
        charges: editingPO.charges || {},
      });
    } else {
      const num = String(Math.floor(Math.random() * 900) + 100);
      setForm((p) => ({ ...p, poNumber: `${p.poPrefix}-${num}` }));
    }
  }, [editingPO]);

  // ── Field setters ───────────────────────────────────────────────────────────
  const setField = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
  };

  const updatePrefix = (prefix) => {
    const upper = prefix.toUpperCase();
    setForm((p) => ({
      ...p,
      poPrefix: upper,
      poNumber: p.poNumber.startsWith(p.poPrefix)
        ? upper + p.poNumber.slice(p.poPrefix.length)
        : p.poNumber,
    }));
  };

  // ── Line helpers ────────────────────────────────────────────────────────────
  const addLine = () =>
    setForm((p) => ({ ...p, lines: [...p.lines, EMPTY_LINE()] }));

  const removeLine = (id) =>
    setForm((p) => ({ ...p, lines: p.lines.filter((l) => l.id !== id) }));

  const updateLine = (id, field, value) =>
    setForm((p) => ({
      ...p,
      lines: p.lines.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, [field]: value };
        if (field === "vatRate") {
          updated.showCustomVat = value === "custom";
          if (value !== "custom") updated.customVat = "";
        }
        return updated;
      }),
    }));

  // ── Charge helpers ──────────────────────────────────────────────────────────
  const toggleCharge = (key) =>
    setForm((p) => ({
      ...p,
      enabledCharges: { ...p.enabledCharges, [key]: !p.enabledCharges[key] },
    }));

  const setCharge = (key, value) =>
    setForm((p) => ({ ...p, charges: { ...p.charges, [key]: value } }));

  // ── Totals ──────────────────────────────────────────────────────────────────
  const lineCalcs = form.lines.map(calcLine);
  const subtotal = lineCalcs.reduce((s, c) => s + c.afterDiscount, 0);
  const totalVat = lineCalcs.reduce((s, c) => s + c.vatAmount, 0);
  const totalDiscount = lineCalcs.reduce((s, c) => s + c.discountAmount, 0);
  const additionalAdd = ADDITIONAL_CHARGES.filter(
    (c) => c.type === "add" && form.enabledCharges[c.key],
  ).reduce((s, c) => s + (parseFloat(form.charges[c.key]) || 0), 0);
  const additionalDeduct = ADDITIONAL_CHARGES.filter(
    (c) => c.type === "deduct" && form.enabledCharges[c.key],
  ).reduce((s, c) => s + (parseFloat(form.charges[c.key]) || 0), 0);
  const netAmount = subtotal + totalVat + additionalAdd - additionalDeduct;

  // ── Validation ──────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.vendorId) e.vendorId = "Vendor is required.";
    if (!form.date) e.date = "Date is required.";
    if (!form.poNumber.trim()) e.poNumber = "PO number is required.";
    if (!form.lines.length) e.lines = "At least one line item is required.";
    form.lines.forEach((l, i) => {
      if (!l.description.trim()) e[`desc_${i}`] = "Description required.";
      if (!l.rate || parseFloat(l.rate) <= 0) e[`rate_${i}`] = "Rate required.";
    });
    return e;
  };

  const handleGenerate = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      toast.error(t("purchaseOrderWizard.saveError"));
      return;
    }
    setSaving(true);
    try {
      const lineItems = form.lines.map((l) => {
        const c = calcLine(l);
        return {
          description: (l.description || "").trim(),
          quantity: parseFloat(l.qty) || 0,
          unitPrice: parseFloat(l.rate) || 0,
          discountAmount: c.discountAmount,
          taxRate: c.vatRate,
        };
      });

      // Backend PO has no reference/charges fields — fold them into notes.
      const noteParts = [];
      if (form.reference) noteParts.push(`Ref: ${form.reference}`);
      Object.entries(form.charges || {}).forEach(([k, v]) => {
        if (v && form.enabledCharges?.[k]) noteParts.push(`${k}: ${v}`);
      });
      const notes = noteParts.join(" | ");

      if (editingPO?.id) {
        await updatePO.mutateAsync({
          id: editingPO.id,
          lineItems,
          notes,
          issueDate: form.date,
        });
        toast.success(t("purchaseOrderWizard.successUpdated"));
      } else {
        await createPO.mutateAsync({
          vendorId: form.vendorId,
          issueDate: form.date,
          currency: "SAR",
          notes,
          lineItems,
        });
        toast.success(t("purchaseOrderWizard.successCreated"));
      }
      navigate("/purchase-orders");
    } catch (err) {
      toast.error(err?.message || t("purchaseOrderWizard.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const fmt = (n) => n.toLocaleString("en-SA", { minimumFractionDigits: 2 });

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-300 ease-in-out ${isCollapsed ? "md:ms-16" : "md:ms-64"}`}
    >
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* ── Sticky Header — matches WizardHeader ── */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="sticky top-0 z-40 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 mb-8"
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate("/purchase-orders")}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                >
                  <Icon name="ArrowLeft" size={20} />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-foreground dark:text-white">
                    {editingPO
                      ? t("purchaseOrderWizard.editTitle")
                      : t("purchaseOrderWizard.newTitle")}
                  </h1>
                  <p className="text-sm text-muted-foreground dark:text-gray-300">
                    Fill in the details below and generate the PO
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Eye"
                  onClick={() => toast("Preview coming soon")}
                >
                  Preview
                </Button>
                <Button
                  variant="outline"
                  size="md"
                  onClick={() => navigate("/purchase-orders")}
                >
                  {t("purchaseOrderWizard.cancel")}
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  iconName={saving ? "Loader2" : "FileCheck"}
                  onClick={handleGenerate}
                  disabled={saving}
                  className="shadow-sm"
                >
                  {saving
                    ? t("purchaseOrderWizard.generating")
                    : editingPO
                      ? t("purchaseOrderWizard.updateShort")
                      : t("purchaseOrderWizard.generate")}
                </Button>
              </div>
            </div>

            {/* Step pills — mirrors invoice wizard step bar */}
            <div className="flex items-center gap-2">
              {[
                { label: "Vendor Details", icon: "Truck" },
                { label: "PO Details", icon: "FileText" },
                { label: "Line Items", icon: "List" },
                { label: "Charges", icon: "PlusCircle" },
                { label: "Summary", icon: "BarChart2" },
              ].map((step, i) => (
                <React.Fragment key={step.label}>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground dark:text-gray-400">
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon
                        name={step.icon}
                        size={13}
                        className="text-primary"
                      />
                    </div>
                    <span className="hidden md:block font-medium text-foreground dark:text-white">
                      {step.label}
                    </span>
                  </div>
                  {i < 4 && (
                    <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600 hidden md:block" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </motion.div>

        {/* ── Two-column layout — 3/4 main + 1/4 sidebar ── */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* ── LEFT: Main form ── */}
          <div className="lg:col-span-3 space-y-6">
            {/* ── Important Notice ── */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="flex gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-800"
            >
              <Icon
                name="AlertCircle"
                size={18}
                className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
              />
              <p className="text-sm text-amber-800 dark:text-amber-300">
                <span className="font-semibold">Important: </span>A Purchase
                Order is <span className="font-medium">not</span> an actual
                purchase. It is an order placed with a vendor. If you have
                already made a purchase, go to{" "}
                <button
                  onClick={() => navigate("/expenses")}
                  className="underline font-semibold hover:text-amber-900 transition-colors"
                >
                  Expenses
                </button>{" "}
                and click <span className="font-medium">"Add New Expense"</span>{" "}
                to record a purchase bill.
              </p>
            </motion.div>

            {/* ── Section 1: Vendor Details ── */}
            <SectionCard
              icon="Truck"
              iconBg="bg-blue-100 dark:bg-blue-900/20"
              iconColor="text-blue-600 dark:text-blue-400"
              title="Vendor Details"
              subtitle="Who is this purchase order addressed to?"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field
                  label="To"
                  hint="Company or person this PO is addressed to"
                >
                  <TextInput
                    value={form.to}
                    placeholder="e.g. Procurement Department"
                    onChange={(e) => setField("to", e.target.value)}
                  />
                </Field>
                <Field label="Vendor" required error={errors.vendorId}>
                  <select
                    value={form.vendorId}
                    onChange={(e) => {
                      const v = vendors.find(
                        (x) => String(x.id || x._id) === e.target.value,
                      );
                      setForm((p) => ({
                        ...p,
                        vendorId: e.target.value,
                        vendorName: v?.name || "",
                      }));
                      if (errors.vendorId)
                        setErrors((p) => ({ ...p, vendorId: "" }));
                    }}
                    className={`w-full px-3 py-2.5 border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent text-sm ${errors.vendorId ? "border-red-500" : "border-border"}`}
                  >
                    <option value="">Select vendor</option>
                    {vendors.map((v) => (
                      <option key={v.id || v._id} value={v.id || v._id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </SectionCard>

            {/* ── Section 2: PO Details ── */}
            <SectionCard
              icon="FileText"
              iconBg="bg-primary-100 dark:bg-primary-900/20"
              iconColor="text-primary"
              title="Purchase Order Details"
              subtitle="Configure the PO number, date and reference"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* PO Number with prefix */}
                <Field
                  label="PO Number"
                  required
                  error={errors.poNumber}
                  hint="Set your own prefix (e.g. NYC, ABD, PO)"
                >
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={form.poPrefix}
                      maxLength={6}
                      onChange={(e) => updatePrefix(e.target.value)}
                      placeholder="PO"
                      className="w-20 px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-foreground font-mono text-sm uppercase focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    <TextInput
                      value={form.poNumber}
                      placeholder="e.g. PO-001"
                      error={errors.poNumber}
                      onChange={(e) => setField("poNumber", e.target.value)}
                      className="flex-1 font-mono"
                    />
                  </div>
                  {errors.poNumber && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.poNumber}
                    </p>
                  )}
                </Field>

                <Field label="Date" required error={errors.date}>
                  <TextInput
                    type="date"
                    value={form.date}
                    error={errors.date}
                    onChange={(e) => setField("date", e.target.value)}
                  />
                </Field>

                <Field
                  label="Reference Number"
                  hint="Optional — internal or vendor reference"
                >
                  <TextInput
                    value={form.reference}
                    placeholder="e.g. REF-2025-001"
                    onChange={(e) => setField("reference", e.target.value)}
                  />
                </Field>
              </div>
            </SectionCard>

            {/* ── Section 3: Line Items ── */}
            <SectionCard
              icon="List"
              iconBg="bg-primary-100 dark:bg-primary-900/20"
              iconColor="text-primary"
              title="Line Items"
              subtitle="Add products or services to this purchase order"
              action={
                <Button
                  variant="primary"
                  size="sm"
                  iconName="Plus"
                  onClick={addLine}
                >
                  Add Item
                </Button>
              }
            >
              <div className="space-y-4">
                {errors.lines && (
                  <p className="text-xs text-red-500">{errors.lines}</p>
                )}

                <AnimatePresence>
                  {form.lines.map((line, index) => {
                    const calc = calcLine(line);
                    return (
                      <motion.div
                        key={line.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.25, delay: index * 0.04 }}
                        className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
                      >
                        {/* Line header */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center">
                              <span className="text-xs font-semibold text-primary">
                                {index + 1}
                              </span>
                            </div>
                            <span className="text-sm font-medium text-foreground dark:text-white">
                              Line Item {index + 1}
                            </span>
                          </div>
                          {form.lines.length > 1 && (
                            <button
                              onClick={() => removeLine(line.id)}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            >
                              <Icon name="Trash2" size={14} />
                            </button>
                          )}
                        </div>

                        {/* Description */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                            Description <span className="text-red-500">*</span>
                          </label>
                          <TextInput
                            value={line.description}
                            placeholder="Product or service description"
                            error={errors[`desc_${index}`]}
                            onChange={(e) =>
                              updateLine(line.id, "description", e.target.value)
                            }
                          />
                          {errors[`desc_${index}`] && (
                            <p className="text-xs text-red-500 mt-1">
                              {errors[`desc_${index}`]}
                            </p>
                          )}
                        </div>

                        {/* Qty / Rate / Discount / VAT / Amount */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                          {/* Qty */}
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground dark:text-gray-400 mb-1.5">
                              Qty
                            </label>
                            <TextInput
                              type="number"
                              value={line.qty}
                              min="0"
                              step="1"
                              onChange={(e) =>
                                updateLine(line.id, "qty", e.target.value)
                              }
                            />
                          </div>

                          {/* Rate */}
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground dark:text-gray-400 mb-1.5">
                              Rate (SR) <span className="text-red-500">*</span>
                            </label>
                            <TextInput
                              type="number"
                              value={line.rate}
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              error={errors[`rate_${index}`]}
                              onChange={(e) =>
                                updateLine(line.id, "rate", e.target.value)
                              }
                            />
                            {errors[`rate_${index}`] && (
                              <p className="text-xs text-red-500 mt-1">
                                {errors[`rate_${index}`]}
                              </p>
                            )}
                          </div>

                          {/* Discount */}
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground dark:text-gray-400 mb-1.5">
                              Discount
                            </label>
                            <div className="flex gap-1">
                              <TextInput
                                type="number"
                                value={line.discount}
                                min="0"
                                placeholder="0"
                                onChange={(e) =>
                                  updateLine(
                                    line.id,
                                    "discount",
                                    e.target.value,
                                  )
                                }
                                className="flex-1"
                              />
                              <button
                                onClick={() =>
                                  updateLine(
                                    line.id,
                                    "discountType",
                                    line.discountType === "percent"
                                      ? "fixed"
                                      : "percent",
                                  )
                                }
                                className="px-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-xs text-muted-foreground hover:bg-gray-50 transition-colors flex-shrink-0"
                                title="Toggle % / SR"
                              >
                                {line.discountType === "percent" ? "%" : "SR"}
                              </button>
                            </div>
                          </div>

                          {/* VAT */}
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground dark:text-gray-400 mb-1.5">
                              VAT %
                            </label>
                            <SelectInput
                              value={line.vatRate}
                              onChange={(e) =>
                                updateLine(
                                  line.id,
                                  "vatRate",
                                  e.target.value === "custom"
                                    ? "custom"
                                    : parseFloat(e.target.value),
                                )
                              }
                            >
                              {VAT_RATES.map((v) => (
                                <option key={v.label} value={v.value}>
                                  {v.label}
                                </option>
                              ))}
                            </SelectInput>
                            {line.showCustomVat && (
                              <TextInput
                                type="number"
                                value={line.customVat}
                                min="0"
                                max="100"
                                placeholder="e.g. 5"
                                onChange={(e) =>
                                  updateLine(
                                    line.id,
                                    "customVat",
                                    e.target.value,
                                  )
                                }
                                className="mt-1 text-xs"
                              />
                            )}
                          </div>

                          {/* Amount */}
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground dark:text-gray-400 mb-1.5">
                              Amount (SR)
                            </label>
                            <div className="px-3 py-2.5 bg-primary-50 dark:bg-primary-900/20 rounded-lg text-primary font-semibold text-sm">
                              {fmt(calc.total)}
                            </div>
                          </div>
                        </div>

                        {/* Line subtotals */}
                        {parseFloat(line.rate) > 0 && (
                          <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground border-t border-gray-200 dark:border-gray-600 pt-3">
                            <span>
                              Subtotal:{" "}
                              <span className="text-foreground font-medium">
                                SR {fmt(calc.subtotal)}
                              </span>
                            </span>
                            {calc.discountAmount > 0 && (
                              <span>
                                Discount:{" "}
                                <span className="text-orange-500 font-medium">
                                  −SR {fmt(calc.discountAmount)}
                                </span>
                              </span>
                            )}
                            {calc.vatAmount > 0 && (
                              <span>
                                VAT ({calc.vatRate}%):{" "}
                                <span className="text-blue-500 font-medium">
                                  SR {fmt(calc.vatAmount)}
                                </span>
                              </span>
                            )}
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </AnimatePresence>

                {/* Add Item CTA */}
                <motion.div className="flex justify-center pt-2">
                  <Button
                    variant="outline"
                    size="md"
                    iconName="Plus"
                    onClick={addLine}
                    className="border-dashed border-2 hover:border-primary hover:bg-primary-50 dark:hover:bg-primary-900/20 w-full"
                  >
                    Add Another Item
                  </Button>
                </motion.div>
              </div>
            </SectionCard>

            {/* ── Section 4: Additional Charges ── */}
            <SectionCard
              icon="PlusCircle"
              iconBg="bg-orange-100 dark:bg-orange-900/20"
              iconColor="text-orange-500"
              title="Additional Charges"
              subtitle="Enable and configure charges or deductions"
              defaultExpanded={false}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {ADDITIONAL_CHARGES.map((charge) => (
                  <div
                    key={charge.key}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all duration-200 ${
                      form.enabledCharges[charge.key]
                        ? "border-primary/40 bg-primary-50 dark:bg-primary-900/10"
                        : "border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50"
                    }`}
                  >
                    {/* Toggle */}
                    <button
                      onClick={() => toggleCharge(charge.key)}
                      className={`w-9 h-5 rounded-full transition-all duration-200 flex-shrink-0 relative ${
                        form.enabledCharges[charge.key]
                          ? "bg-primary"
                          : "bg-gray-300 dark:bg-gray-600"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all duration-200 ${
                          form.enabledCharges[charge.key]
                            ? "left-[18px]"
                            : "left-0.5"
                        }`}
                      />
                    </button>

                    <Icon
                      name={charge.icon}
                      size={15}
                      className={
                        form.enabledCharges[charge.key]
                          ? "text-primary"
                          : "text-muted-foreground"
                      }
                    />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-foreground dark:text-white truncate">
                          {charge.label}
                        </span>
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
                            charge.type === "add"
                              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                              : "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
                          }`}
                        >
                          {charge.type === "add" ? "+ Add" : "− Deduct"}
                        </span>
                      </div>
                    </div>

                    <AnimatePresence>
                      {form.enabledCharges[charge.key] && (
                        <motion.div
                          initial={{ opacity: 0, width: 0 }}
                          animate={{ opacity: 1, width: "auto" }}
                          exit={{ opacity: 0, width: 0 }}
                          className="overflow-hidden flex-shrink-0"
                        >
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                              SR
                            </span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={form.charges[charge.key] || ""}
                              onChange={(e) =>
                                setCharge(charge.key, e.target.value)
                              }
                              className="w-28 pl-7 pr-2 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-foreground text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* ── Bottom Generate Button ── */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
            >
              <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
                <div className="text-sm text-muted-foreground">
                  Review all details before generating the purchase order.
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    size="md"
                    onClick={() => navigate("/purchase-orders")}
                  >
                    {t("purchaseOrderWizard.cancel")}
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    iconName={saving ? "Loader2" : "FileCheck"}
                    onClick={handleGenerate}
                    disabled={saving}
                    className="shadow-sm"
                  >
                    {saving
                      ? t("purchaseOrderWizard.generating")
                      : editingPO
                        ? t("purchaseOrderWizard.update")
                        : t("purchaseOrderWizard.generate")}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>

          {/* ── RIGHT: Summary sidebar — matches ValidationPanel ── */}
          <div className="lg:col-span-1">
            <div className="sticky top-32 space-y-4">
              {/* PO Summary */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
              >
                <div className="flex items-center gap-2 mb-5">
                  <Icon name="BarChart2" size={18} className="text-primary" />
                  <h3 className="text-base font-semibold text-foreground dark:text-white">
                    PO Summary
                  </h3>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground dark:text-gray-300">
                      Subtotal
                    </span>
                    <span className="font-medium text-foreground dark:text-white">
                      SR {fmt(subtotal)}
                    </span>
                  </div>
                  {totalDiscount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground dark:text-gray-300">
                        Discount
                      </span>
                      <span className="font-medium text-orange-500">
                        −SR {fmt(totalDiscount)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground dark:text-gray-300">
                      VAT
                    </span>
                    <span className="font-medium text-foreground dark:text-white">
                      SR {fmt(totalVat)}
                    </span>
                  </div>
                  {additionalAdd > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground dark:text-gray-300">
                        Charges
                      </span>
                      <span className="font-medium text-green-600 dark:text-green-400">
                        +SR {fmt(additionalAdd)}
                      </span>
                    </div>
                  )}
                  {additionalDeduct > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground dark:text-gray-300">
                        Deductions
                      </span>
                      <span className="font-medium text-red-500">
                        −SR {fmt(additionalDeduct)}
                      </span>
                    </div>
                  )}
                  <div className="border-t border-gray-200 dark:border-gray-600 pt-3 mt-1">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-foreground dark:text-white">
                        Net Amount
                      </span>
                      <span className="text-lg font-bold text-primary">
                        SR {fmt(netAmount)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Quick stats */}
                <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-700 space-y-2">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Line Items</span>
                    <span className="font-medium text-foreground">
                      {form.lines.length}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Vendor</span>
                    <span className="font-medium text-foreground truncate max-w-[100px]">
                      {form.vendorName || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>PO #</span>
                    <span className="font-medium text-foreground font-mono">
                      {form.poNumber || "—"}
                    </span>
                  </div>
                </div>
              </motion.div>

              {/* Validation status — matches ValidationPanel */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Icon
                    name="CheckCircle2"
                    size={18}
                    className="text-primary"
                  />
                  <h3 className="text-base font-semibold text-foreground dark:text-white">
                    Checklist
                  </h3>
                </div>

                <div className="space-y-2.5">
                  {[
                    { label: "Vendor name", ok: !!form.vendorName.trim() },
                    { label: "PO number", ok: !!form.poNumber.trim() },
                    { label: "Date set", ok: !!form.date },
                    {
                      label: "Line items",
                      ok:
                        form.lines.length > 0 &&
                        form.lines.every(
                          (l) => l.description.trim() && parseFloat(l.rate) > 0,
                        ),
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <Icon
                          name={item.ok ? "CheckCircle2" : "Circle"}
                          size={15}
                          className={
                            item.ok
                              ? "text-green-500"
                              : "text-gray-300 dark:text-gray-600"
                          }
                        />
                        <span className="text-sm text-muted-foreground dark:text-gray-300">
                          {item.label}
                        </span>
                      </div>
                      <span
                        className={`text-xs font-medium ${item.ok ? "text-green-600 dark:text-green-400" : "text-gray-400"}`}
                      >
                        {item.ok ? "Done" : "Pending"}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Ready indicator */}
                {(() => {
                  const allDone =
                    form.vendorName.trim() &&
                    form.poNumber.trim() &&
                    form.date &&
                    form.lines.length > 0 &&
                    form.lines.every(
                      (l) => l.description.trim() && parseFloat(l.rate) > 0,
                    );
                  return (
                    <div
                      className={`mt-4 p-3 rounded-lg ${
                        allDone
                          ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                          : "bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon
                          name={allDone ? "CheckCircle2" : "Clock"}
                          size={15}
                          className={
                            allDone ? "text-green-600" : "text-gray-400"
                          }
                        />
                        <span
                          className={`text-sm font-medium ${allDone ? "text-green-700 dark:text-green-400" : "text-gray-500"}`}
                        >
                          {allDone ? "Ready to generate" : "Complete the form"}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </motion.div>

              {/* Help card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6"
              >
                <h4 className="font-medium text-foreground dark:text-white mb-4">
                  Need Help?
                </h4>
                <div className="space-y-2.5">
                  {[
                    { label: "PO Guidelines", icon: "BookOpen" },
                    { label: "Tax Information", icon: "Info" },
                    { label: "Contact Support", icon: "HelpCircle" },
                  ].map((item) => (
                    <Button
                      key={item.label}
                      variant="outline"
                      size="sm"
                      iconName={item.icon}
                      className="w-full justify-start text-sm"
                    >
                      {item.label}
                    </Button>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PurchaseOrderWizard = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <POWizardContent />
    </div>
  </SidebarProvider>
);

export default PurchaseOrderWizard;
